// /:partnerId/device-verify?dc=<device_code> — pantalla de aprobacion del TV
// (RFC 8628 + QR flow).
//
// Flujo:
//   1. El SmartTV (app de tercero) muestra un QR apuntando a esta URL.
//   2. El usuario lo escanea con el celular y cae aca (sin sesion previa).
//   3. La pantalla hace lookup del device_code (GET) y muestra el user_code
//      legible (ABCD-EFGH) para que confirme que coincide con el TV.
//   4. El usuario ingresa email+password (sus credenciales AuthSys) + un nombre
//      para el dispositivo (ej "TV Living Casa") y aprueba.
//   5. POST /oauth/device/verify autentica + aprueba en un paso. El TV, que
//      pollea /oauth/token, recibe los tokens en la siguiente vuelta.

import { FormEvent, useEffect, useState } from 'react';
import { normalizeEmail } from '@/lib/email-normalize';
import { useParams, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import {
  approveDeviceCode,
  getDeviceCodeInfo,
  resendVerification,
  isConsentRequired,
  ApiError,
  type DeviceCodeInfo,
  type ConsentRequired,
} from '@/lib/api';
import ConsentTerms from '@/components/ConsentTerms';
import { webmailLink } from '@/lib/webmail';

function errorMessageFor(code: string): string {
  switch (code) {
    case 'user_not_found':
    case 'invalid_credentials':
      // Anti-enumeracion: mismo mensaje en ambos casos.
      return 'Email o contraseña incorrectos.';
    case 'user_blocked':
      return 'Tu cuenta no está habilitada. Si registraste recién, verificá tu email.';
    case 'device_not_found':
    case 'expired_token':
    case 'invalid_grant':
      return 'El código expiró o ya fue usado. Volvé al TV y generá uno nuevo.';
    case 'rate_limited':
      return 'Demasiados intentos. Esperá un momento antes de reintentar.';
    case 'network_error':
      return 'No pudimos conectar. Revisá tu conexión e intentá de nuevo.';
    case 'internal_error':
      return 'Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.';
    default:
      return 'No pudimos aprobar el dispositivo. Volvé a intentar.';
  }
}

export default function DeviceVerify(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [search] = useSearchParams();
  const deviceCode = search.get('dc') ?? '';

  const [info, setInfo] = useState<DeviceCodeInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Email no verificado: el backend rechaza la aprobacion con `email_not_verified`.
  // Mostramos un panel con la opcion de reenviar el correo de verificacion. A
  // diferencia del login, aca no arrastramos authz params (el device flow no
  // redirige a un partner): el usuario verifica y vuelve al TV a reaprobar.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  // CTA al webmail del usuario tras el reenvío (Gmail abre la búsqueda del
  // remitente, que incluye Spam).
  const resendMail = resendState === 'sent' ? webmailLink(email) : null;
  // Re-consentimiento de T&C (ADR 0008): si la aprobacion lo pide, mostramos
  // el interstitial inline; al resolver, el device queda aprobado.
  const [consent, setConsent] = useState<ConsentRequired | null>(null);

  useEffect(() => {
    if (!deviceCode) {
      setInfoLoading(false);
      return;
    }
    let cancelled = false;
    getDeviceCodeInfo(partnerId, deviceCode)
      .then((res) => {
        if (!cancelled) setInfo(res);
      })
      .catch(() => {
        // Si el lookup falla, no bloqueamos: dejamos intentar aprobar igual.
        if (!cancelled) setInfo(null);
      })
      .finally(() => {
        if (!cancelled) setInfoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId, deviceCode]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!deviceCode) return;
    setLoading(true);
    setErr(null);
    setNeedsVerification(false);
    setResendState('idle');
    try {
      const trimmedName = deviceName.trim();
      const result = await approveDeviceCode(partnerId, {
        deviceCode,
        email: normalizeEmail(email),
        password,
        ...(trimmedName ? { deviceName: trimmedName } : {}),
      });
      if (isConsentRequired(result)) {
        setConsent(result);
        return;
      }
      setApproved(true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      if (code === 'email_not_verified') {
        // Credenciales OK pero email sin verificar: ofrecemos reenviar.
        setNeedsVerification(true);
      } else {
        setErr(errorMessageFor(code));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend(): Promise<void> {
    setResendState('sending');
    try {
      await resendVerification(partnerId, { email: normalizeEmail(email) });
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  }

  if (!deviceCode) {
    return (
      <AuthShell
        title="Código faltante"
        subtitle="Escaneá el QR desde tu TV para aprobar el dispositivo."
      >
        <div
          role="alert"
          className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error text-center"
        >
          El link no incluye el código del dispositivo.
        </div>
      </AuthShell>
    );
  }

  if (consent) {
    return (
      <ConsentTerms
        partnerId={partnerId}
        ticket={consent.consent_ticket}
        tos={consent.tos}
        onResolved={() => {
          // En device flow consentAccept devuelve { approved: true }.
          setConsent(null);
          setApproved(true);
        }}
      />
    );
  }

  if (approved) {
    return (
      <AuthShell
        title="¡Dispositivo aprobado!"
        subtitle="Ya podés volver al TV. Va a iniciar sesión en unos segundos."
      >
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-text-muted/20 p-4 text-sm text-text-muted text-center"
        >
          Podés cerrar esta pestaña.
        </div>
      </AuthShell>
    );
  }

  // Código ya resuelto (aprobado/consumido) o vencido/inexistente → no hay form.
  const terminal = info && info.status !== 'pending';
  if (terminal) {
    const alreadyDone = info.status === 'approved' || info.status === 'consumed';
    return (
      <AuthShell
        title={alreadyDone ? 'Dispositivo ya aprobado' : 'Código no válido'}
        subtitle={
          alreadyDone
            ? 'Este dispositivo ya fue vinculado.'
            : 'El código expiró o no existe.'
        }
      >
        <div
          role="alert"
          className="rounded-lg border border-text-muted/20 p-4 text-sm text-text-muted text-center"
        >
          {alreadyDone
            ? 'Podés cerrar esta pestaña.'
            : 'Volvé al TV y generá un código nuevo.'}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Vincular dispositivo"
      subtitle="Ingresá con tu cuenta para autorizar tu TV."
    >
      <form onSubmit={onSubmit} aria-busy={loading ? 'true' : undefined} className="space-y-4" noValidate>
        <div className="rounded-lg border border-text-muted/20 p-4 text-sm">
          <p className="text-text-muted">Vas a vincular el dispositivo con código:</p>
          <p className="font-mono text-lg font-semibold tracking-widest text-text mt-1" aria-label="Código del dispositivo">
            {infoLoading ? '····-····' : (info?.user_code ?? '—')}
          </p>
          <p className="text-xs text-text-muted mt-1">
            Verificá que coincida con el que muestra tu TV.
          </p>
        </div>
        <FormField
          name="email"
          type="email"
          label="Email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FormField
          name="password"
          type="password"
          label="Contraseña"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormField
          name="device_name"
          type="text"
          label="Nombre del dispositivo (opcional)"
          placeholder="Ej: TV Living Casa"
          maxLength={80}
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
        />
        {err ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            {err}
          </div>
        ) : null}
        {needsVerification ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-text space-y-2"
          >
            <p>
              Tu e-mail no está verificado todavía. Revisá tu casilla y hacé clic
              en el link de verificación para poder vincular el dispositivo.
            </p>
            {resendState === 'sent' ? (
              <div className="space-y-2">
                <p className="text-primary">
                  Te reenviamos el correo de verificación. Revisá tu casilla (y spam).
                  Los links anteriores siguen sirviendo: cualquiera activa tu cuenta.
                </p>
                {resendMail ? (
                  <a
                    href={resendMail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-md border border-accent px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10"
                  >
                    Abrir {resendMail.label}
                  </a>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendState === 'sending'}
                className="underline hover:no-underline disabled:opacity-60"
              >
                {resendState === 'sending'
                  ? 'Reenviando…'
                  : 'Reenviar correo de verificación'}
              </button>
            )}
            {resendState === 'error' ? (
              <p className="text-error">
                No pudimos reenviarlo ahora. Probá de nuevo en un momento.
              </p>
            ) : null}
          </div>
        ) : null}
        <SubmitButton loading={loading} loadingLabel="Vinculando…">
          Vincular dispositivo
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
